import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  apiDocGroupUrl,
  apiDocUrl,
  apiIntegrationShellQuery,
  createApiKey,
  saveApiKeyScopes,
  type ApiDocument,
  type ApiIntegrationShell,
  type ApiScopeGroup,
  type KeyApprovalInput,
} from '../../api/settingsApiIntegration'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './ApiIntegrationPage.css'

/**
 * Settings -> INTEGRARE -> API Integration, element cu element fata de
 * `views/version-3/settings/companies/my_company/api_integration.php`.
 *
 * CHEIA PROASPATA SE ARATA SI ECRANUL NU SE REINCARCA. In tabela raman doar
 * amprenta si primele caractere, deci clipa de dupa generare e singurul moment
 * in care cheia exista undeva citibila - o reimprospatare ar sterge tocmai
 * locul ala. De-aia se spune pe fata ca nu se mai arata: altfel omul inchide
 * fila si se intoarce dupa ea maine.
 *
 * Restul ecranului se innoieste totusi, fiindca POST-ul raspunde cu el intreg:
 * prefixul nou, data, cine a facut-o. Doar cheia sta separat, in caseta ei.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'The key could not be generated.'
}

/** `substr(..., 0, 16)` din macheta: "2026-09-08 09:30". */
export function shortStamp(value: string): string {
  return String(value ?? '').substring(0, 16)
}

/** Documentele grupate cum le grupeaza api-ul, in ordinea in care au venit. */
export function groupDocs(docs: ApiDocument[]): Array<[string, ApiDocument[]]> {
  const out = new Map<string, ApiDocument[]>()
  for (const doc of docs) {
    const key = doc.group !== '' ? doc.group : 'Documents'
    const list = out.get(key)
    if (list) list.push(doc)
    else out.set(key, [doc])
  }

  return [...out.entries()]
}

/** Textul spus cand cheia n-are niciun drept - api-ul o refuza pe capetele astea. */
export const NO_SCOPES = 'No scopes - refused on CMDB/ADM/Catalog'

/**
 * Bifele drepturilor, pe familii, cu lista EXACTA din api (`scopeCatalog`).
 * Ce bifeaza omul pleaca in `scopes[]`; serverul pastreaza numai ce cunoaste.
 */
export function ScopePicker({
  catalog,
  value,
  onChange,
  disabled = false,
}: {
  catalog: ApiScopeGroup[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  const toggle = (scope: string, on: boolean) => {
    onChange(on ? [...value.filter((s) => s !== scope), scope] : value.filter((s) => s !== scope))
  }

  return (
    <div className="ak-scopes" id="akScopes">
      {catalog.map((group) => (
        <fieldset className="ak-scopes__group" key={group.group}>
          <legend>{group.group}</legend>
          {group.scopes.map((one) => (
            <label className="ak-scopes__item" key={one.scope}>
              <input
                type="checkbox"
                name="scopes[]"
                value={one.scope}
                checked={value.includes(one.scope)}
                disabled={disabled}
                onChange={(e) => toggle(one.scope, e.target.checked)}
              />{' '}
              {one.label} <span className="ak-doc__slug">{one.scope}</span>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  )
}

/** Drepturile din lista care cer aprobare (administrare / ADM), cu politica aprinsa. */
export function needsApproval(page: ApiIntegrationShell, scopes: string[]): string[] {
  if (!page.adminSsoRequired) return []
  const privileged = page.privilegedScopes ?? []

  return scopes.filter((scope) => privileged.includes(scope))
}

/**
 * Aprobarea unei chei de administrare (`KeyApproval` pe server): contul de
 * serviciu si termenul. O da numai un administrator intrat prin SSO - ceilalti
 * vad de ce nu pot.
 */
export function ApprovalFields({
  page,
  scopes,
  value,
  onChange,
  disabled = false,
}: {
  page: ApiIntegrationShell
  scopes: string[]
  value: KeyApprovalInput
  onChange: (next: KeyApprovalInput) => void
  disabled?: boolean
}) {
  const privileged = needsApproval(page, scopes)
  if (privileged.length === 0) return null
  const max = page.maxKeyDays ?? 90

  return (
    <div className="alert alert-warning ak-approval" id="akApproval">
      <p style={{ marginTop: 0 }}>
        <i className="fas fa-user-shield"></i> This company requires single sign-on for administrators.{' '}
        {privileged.join(', ')} {privileged.length === 1 ? 'needs' : 'need'} a key approved for a service account, for at
        most {max} days. Every call is recorded in the API log.
      </p>
      {page.adminSsoKeyEnforced === false && (
        <p className="text-muted" id="akApprovalMeasured">
          <i className="fas fa-info-circle"></i> For now this is only recorded: the key is saved and works without the
          approval, until the platform turns enforcement on.
        </p>
      )}
      {page.canApprove ? (
        <div className="ak-approval__fields">
          <label>
            Service account{' '}
            <select
              id="akServiceUser"
              value={value.serviceUserId || ''}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, serviceUserId: Number(e.target.value) || 0 })}
            >
              <option value="">Choose...</option>
              {(page.serviceAccounts ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </label>{' '}
          <label>
            Valid for{' '}
            <input
              id="akExpiresDays"
              type="number"
              min={1}
              max={max}
              value={value.expiresDays}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, expiresDays: Number(e.target.value) || 0 })}
              style={{ width: 70 }}
            />{' '}
            days
          </label>
        </div>
      ) : (
        <p className="text-danger" id="akApprovalRefused" style={{ marginBottom: 0 }}>
          Only an administrator signed in with single sign-on can approve it. Sign in with your organisation&apos;s button.
        </p>
      )}
    </div>
  )
}

function FreshKey({ value }: { value: string }) {
  const copy = () => {
    /* `clipboard` lipseste pe legaturi necriptate si in browsere vechi; atunci
       se selecteaza textul si omul apasa Ctrl+C singur, in loc sa apese un
       buton care tace. */
    if (navigator.clipboard && navigator.clipboard.writeText) {
      void navigator.clipboard.writeText(value).then(() => toast('The key is in your clipboard.', 'API Integration'))

      return
    }

    const node = document.getElementById('akFreshKey')
    if (!node) return
    const range = document.createRange()
    range.selectNodeContents(node)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  return (
    <div className="ak-fresh" id="akFresh">
      <h4>
        <i className="fas fa-key"></i> Your key
      </h4>
      <p style={{ marginBottom: 8 }}>
        <span className="ak-key" id="akFreshKey">
          {value}
        </span>
      </p>
      <button className="btn btn-default btn-xs" id="akCopy" type="button" onClick={copy}>
        <i className="far fa-copy"></i> Copy
      </button>
      <p className="ak-note" style={{ margin: '10px 0 0' }}>
        Shown once. It is never stored in the clear, so it cannot be recovered &mdash; whoever loses it generates a new
        one, and this one stops working.
      </p>
    </div>
  )
}

export function ApiIntegrationPage() {
  const query = useQueryClient()
  const shell = useQuery(apiIntegrationShellQuery)
  const [live, setLive] = useState<ApiIntegrationShell | null>(null)
  const [fresh, setFresh] = useState('')
  const [busy, setBusy] = useState(false)
  /* Bifele pentru cheia noua, si cele ale cheii active cand se editeaza. */
  const [picked, setPicked] = useState<string[]>([])
  const [editing, setEditing] = useState<string[] | null>(null)
  const [approval, setApproval] = useState<KeyApprovalInput>({ serviceUserId: 0, expiresDays: 0 })

  const page = live ?? shell.data ?? null

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!page) return <Spinner />

  const catalog = page.scopeCatalog ?? []
  const keyScopes = page.key?.scopes ?? []
  const days = approval.expiresDays || page.maxKeyDays || 90
  /* Aprobarea pleaca numai cand a ales cineva contul de serviciu. */
  const approvalToSend = approval.serviceUserId > 0 ? { serviceUserId: approval.serviceUserId, expiresDays: days } : undefined

  const saveScopes = () => {
    if (editing === null) return
    setBusy(true)
    void saveApiKeyScopes(editing, approvalToSend)
      .then((next) => {
        setBusy(false)
        setEditing(null)
        setLive(next)
        void query.invalidateQueries({ queryKey: ['settings', 'api_integration'] })
        toast('Scopes saved. The key itself did not change.', 'API Integration')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(
          error instanceof ApiError && error.message ? error.message : 'The scopes could not be saved.',
          'API Integration',
          'error',
        )
      })
  }

  const make = () => {
    if (page.key && !window.confirm('The current key stops working immediately, and so does anything using it. Continue?')) {
      return
    }

    setBusy(true)
    /* Cheia noua pleaca cu bifele alese; o inlocuire le pastreaza pe ale celei vechi. */
    void createApiKey(page.key === null ? picked : keyScopes, approvalToSend)
      .then((next) => {
        setBusy(false)
        setFresh(next.key)
        /* `key` din raspuns e cheia in clar, nu forma ei din ecran - deci
           ecranul se reciteste, iar cheia proaspata ramane in caseta ei. */
        setLive(null)
        void query.invalidateQueries({ queryKey: ['settings', 'api_integration'] })
        toast('Key generated. Copy it now, it is not shown again.', 'API Integration')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'API Integration', 'error')
      })
  }

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {page.tabs.length > 0 ? <li className="menu-header">{page.tabs[0].group}</li> : null}
          {page.tabs.map((tab) => (
            <li className={tab.id === 'api_integration' ? 'menu-tab active' : 'menu-tab'} key={tab.id}>
              <a
                id={tab.id}
                href={`/app/${tab.href}`}
                onClick={(e) => {
                  if (tab.id === 'api_integration') e.preventDefault()
                }}
              >
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        <div id="content">
          <h3 style={{ marginTop: 0 }}>API Integration</h3>
          <p className="ak-note" style={{ marginBottom: 18 }}>
            {page.apiUrl === '' ? 'The API address is not known on this installation.' : `${page.apiUrl}/api/v4`}
          </p>

          {fresh !== '' ? <FreshKey value={fresh} /> : null}

          <div className="ak-card">
            <h4>Authorisation key</h4>

            {page.key === null ? (
              <>
                <p className="ak-note">
                  This company has no key yet. Until it has one, the API answers nobody on its behalf.
                </p>
                {page.canWrite && fresh === '' ? (
                  <>
                    <p className="ak-note">What the key may do on the API:</p>
                    <ScopePicker catalog={catalog} value={picked} onChange={setPicked} disabled={busy} />
                    <ApprovalFields
                      page={page}
                      scopes={picked}
                      value={{ ...approval, expiresDays: days }}
                      onChange={setApproval}
                      disabled={busy}
                    />
                    <button className="btn btn-info btn-sm" id="akMake" type="button" disabled={busy} onClick={make}>
                      <i className="fas fa-key"></i> Generate key
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <table className="table" style={{ marginBottom: 12 }}>
                  <tbody>
                    <tr>
                      <td style={{ width: 150 }} className="ak-note">
                        Key
                      </td>
                      <td className="ak-key">{page.key.prefix}&hellip;</td>
                    </tr>
                    <tr>
                      <td className="ak-note">Generated</td>
                      <td>
                        {shortStamp(page.key.createdAt)}
                        {page.key.createdBy !== '' ? <span className="ak-note"> by {page.key.createdBy}</span> : null}
                      </td>
                    </tr>
                    <tr>
                      <td className="ak-note">Last used</td>
                      <td>
                        {/* "Never" e o informatie: fie integrarea n-a fost
                            pornita, fie cheia nu mai e a nimanui. */}
                        {page.key.lastUsedAt === '' ? (
                          <span className="ak-note">never</span>
                        ) : (
                          shortStamp(page.key.lastUsedAt)
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="ak-note">Scopes</td>
                      <td id="akKeyScopes">
                        {/* NULL pe cheie = niciun drept pentru api, nu toate.
                            Cheile facute inainte de bife sunt asa si nu se
                            schimba singure. */}
                        {keyScopes.length === 0 ? (
                          <span className="text-danger">
                            <i className="fas fa-exclamation-triangle"></i> {NO_SCOPES}
                          </span>
                        ) : (
                          keyScopes.map((scope) => (
                            <span className="ak-key ak-scope" key={scope}>
                              {scope}
                            </span>
                          ))
                        )}
                      </td>
                    </tr>
                    {page.key.serviceUserId ? (
                      <tr>
                        <td className="ak-note">Service account</td>
                        <td id="akKeyApproval">
                          {page.key.serviceUser || `#${page.key.serviceUserId}`}
                          <span className="ak-note">
                            {' '}
                            approved{page.key.approvedBy ? ` by ${page.key.approvedBy}` : ''} {shortStamp(page.key.approvedAt ?? '')}, until{' '}
                            {shortStamp(page.key.expiresAt ?? '')}
                          </span>
                          {page.key.approvalValid ? null : (
                            <span className="text-danger"> &mdash; expired, administrative calls are refused</span>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>

                {page.canWrite && editing !== null ? (
                  <div style={{ marginBottom: 12 }}>
                    <ScopePicker catalog={catalog} value={editing} onChange={setEditing} disabled={busy} />
                    <ApprovalFields
                      page={page}
                      scopes={editing}
                      value={{ ...approval, expiresDays: days }}
                      onChange={setApproval}
                      disabled={busy}
                    />
                    <button
                      className="btn btn-info btn-sm"
                      id="akScopesSave"
                      type="button"
                      disabled={busy}
                      onClick={saveScopes}
                    >
                      <i className="fas fa-check"></i> Save scopes
                    </button>{' '}
                    <button className="btn btn-default btn-sm" type="button" disabled={busy} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                ) : null}

                {page.canWrite ? (
                  <>
                    {editing === null ? (
                      <>
                        <button
                          className="btn btn-default btn-sm"
                          id="akScopesEdit"
                          type="button"
                          disabled={busy}
                          onClick={() => setEditing(keyScopes)}
                        >
                          <i className="fas fa-pen"></i> Edit scopes
                        </button>{' '}
                      </>
                    ) : null}
                    <button className="btn btn-default btn-sm" id="akMake" type="button" disabled={busy} onClick={make}>
                      <i className="fas fa-sync"></i> Generate a new one
                    </button>
                    <span className="ak-note" style={{ marginLeft: 8 }}>
                      The current key stops working immediately, and so does anything using it.
                    </span>
                  </>
                ) : null}
              </>
            )}
          </div>

          <div className="ak-card">
            <h4>Documentation</h4>
            <p className="ak-note" style={{ marginBottom: 12 }}>
              {/*
                Servita de api, nu tinuta aici. O copie in portal ar fi trebuit
                reimprospatata de mana, iar clipa in care nu mai e reimprospatata
                nu se vede din nimic: documentul arata la fel, doar ca minte.
              */}
              Served by the API, so it always matches the version you talk to.
            </p>

            {page.docsError !== '' ? (
              <div className="alert alert-warning" style={{ marginBottom: 0 }}>
                <i className="fas fa-exclamation-circle"></i> {page.docsError}
              </div>
            ) : page.docs.length === 0 ? (
              <p className="ak-note" style={{ margin: 0 }}>
                The API returned no documents.
              </p>
            ) : (
              groupDocs(page.docs).map(([group, items]) => (
                <div key={group}>
                  <div className="ak-group">
                    <h5>{group}</h5>
                    <span className="ak-spacer"></span>
                    {/* Un zip pe familie, nu unul cu tot: cine leaga un agent
                        n-are ce face cu documentul catalogului de servicii. */}
                    <a className="btn btn-link btn-xs" href={apiDocGroupUrl(group)}>
                      <i className="fas fa-file-archive"></i> Download this group ({items.length})
                    </a>
                  </div>
                  {items.map((doc) => (
                    <div className="ak-doc" key={doc.slug}>
                      <span className="ak-doc__name">{doc.title}</span>
                      <span className="ak-doc__slug">{doc.slug}</span>
                      <span className="ak-spacer"></span>
                      <a className="btn btn-link btn-xs" href={apiDocUrl(doc.slug)}>
                        <i className="fas fa-download"></i> Download
                      </a>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  )
}
